import { t, Trans } from '@lingui/macro';
import React, { FunctionComponent, useState, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import moment from 'moment';
import 'moment-timezone';
import Datetime from 'react-datetime';

import { SelectorTypes } from '@components/bulk_management/automation/AutomationEnums';
import { StandardRadioButtonGroup } from '@components/library/radio_button_group/standard/main';
import { ListDropdown } from '@components/library/dropdown';
import Label from '@components/library/labels/PrimaryLabel';
import {
  ensureReactDatetimeA11yEnhancements,
  renderAccessibleDatetimeDay
} from '@components/library/date_time_picker/reactDatetimeA11y';
import { FontIcons } from '@components/library/icon';
import { TextWarningButton } from '@components/library/button';
import './styles/asset-action-selector.scss';

interface AssetActionSelectorProps {
    actionType: SelectorTypes.AssetAvailability | SelectorTypes.AssetExpiration | SelectorTypes.Asset;
    isSectionDisabled?: boolean;
    selectedDate?: string;
    selectedTime?: string;
    selectedTimezone?: string;
    selectedRadioOption?: string;
    onValidationChange?: (isValid: boolean) => void;
    updateAssetAction?: (assetAction: any) => void;
    triggerValues?: {};
    uniqueId?: string;
}

export const AssetActionSelector: FunctionComponent<AssetActionSelectorProps> = ({
    actionType,
    isSectionDisabled = false,
    selectedDate,
    selectedTime,
    selectedTimezone,
    selectedRadioOption,
    onValidationChange,
    updateAssetAction,
    triggerValues, // will be used for default values when specific-time is selected
    uniqueId
}) => {

    const baseSelectedOption = selectedRadioOption || 'trigger-criteria';
    const selectedOption = baseSelectedOption; // Keep original for logic, but use unique IDs for radio buttons
    const currentDate = selectedDate || '';
    const currentTime = selectedTime || '';
    const currentTimezone = selectedTimezone || '';

    useEffect(() => {
        ensureReactDatetimeA11yEnhancements();
    }, []);

    // Get current date/time values as defaults
    const getCurrentDateTimeValues = () => {
        const now = moment();
        return {
            date: now.format('MM/DD/YYYY'),
            time: now.format('hh:mm a')
        };
    };

    // Set default timezone when component loads with 'specific-time' selected
    useEffect(() => {
        if (selectedOption === 'specific-time' && !currentTimezone && updateAssetAction) {
            const currentDateTime = getCurrentDateTimeValues();
            const defaultTimezone = moment.tz.guess();

            updateAssetAction({
                selectedOption: 'specific-time',
                date: currentDate || currentDateTime.date,
                time: currentTime || currentDateTime.time,
                timezone: defaultTimezone
            });
        }
    }, [selectedOption, currentTimezone, currentDate, currentTime, updateAssetAction]);

    // Helper function to create a moment object from date, time, and timezone
    const createMomentFromDateTime = (date: string, time: string, timezone: string): moment.Moment | null => {
        if (!date || !time || !timezone) return null;
        try {
            const dateTimeStr = `${date} ${time}`;
            return moment.tz(dateTimeStr, 'MM/DD/YYYY hh:mm a', timezone);
        } catch (e) {
            return null;
        }
    };

    // Validation logic
    const isFormValid = () => {
        if (selectedOption === 'trigger-criteria') {
            return true;
        }
        if (selectedOption === 'specific-time') {
            if (!currentDate || !currentTime || !currentTimezone) {
                return false;
            }

            // Check if date is in the past
            const currentDateTime = createMomentFromDateTime(currentDate, currentTime, currentTimezone);
            if (!currentDateTime) return false;

            // Compare with current time in the same timezone
            const nowInTimezone = moment.tz(currentTimezone || moment.tz.guess());
            // Allow dates that are same or after current time (not in the past)
            if (currentDateTime.isBefore(nowInTimezone)) {
                return false;
            }

            return true;
        }
        return false;
    };

    // Notify parent component about validation state
    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(isFormValid());
        }
    }, [selectedOption, currentDate, currentTime, currentTimezone, onValidationChange]);

    const radioButtons = [
        {
            id: `trigger-criteria-${uniqueId || ''}`,
            copy: actionType === SelectorTypes.AssetAvailability
                ? t`Asset becomes available when trigger criteria is met`
                : t`Asset becomes expired when trigger criteria is met`
        },
        {
            id: `specific-time-${uniqueId || ''}`,
            copy: actionType === SelectorTypes.AssetAvailability
                ? t`Asset becomes available at the time set below`
                : t`Asset becomes expired at the time set below`
        }
    ];

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // Extract the base option from the unique ID (remove the uniqueId suffix)
        // The value will be like "trigger-criteria-{uniqueId}" or "specific-time-{uniqueId}"
        const baseValue = value.startsWith('trigger-criteria') ? 'trigger-criteria' : 'specific-time';

        if (updateAssetAction) {
            if (baseValue === 'trigger-criteria') {
                // Clear all date/time/timezone fields
                updateAssetAction({
                    selectedOption: 'trigger-criteria',
                    date: '',
                    time: '',
                    timezone: ''
                });
            } else {
                // Set specific-time option with current values or current date/time as defaults
                const currentDateTime = getCurrentDateTimeValues();
                const defaultDate = currentDate || currentDateTime.date;
                const defaultTime = currentTime || currentDateTime.time;
                const defaultTimezone = currentTimezone || moment.tz.guess();

                updateAssetAction({
                    selectedOption: 'specific-time',
                    date: defaultDate,
                    time: defaultTime,
                    timezone: defaultTimezone
                });
            }
        }
    };

    const handleDateTimeUpdate = (date: string, time: string, timezone: string) => {
        if (updateAssetAction) {
            updateAssetAction({
                selectedOption: selectedOption,
                date: date,
                time: time,
                timezone: timezone
            });
        }
    };


    const clearDateTimeData = () => {
        // Clear all date/time/timezone data
        if (updateAssetAction) {
            updateAssetAction({
                selectedOption: selectedOption,
                date: '',
                time: '',
                timezone: ''
            });
        }
    };


    return (
        <div className="asset-action-selector">
            <div className="radio-button-section">
                <StandardRadioButtonGroup
                    name={`${actionType}-options-${uniqueId || ''}`}
                    radioButtons={radioButtons}
                    checkedId={baseSelectedOption === 'trigger-criteria' ? `trigger-criteria-${uniqueId || ''}` : `specific-time-${uniqueId || ''}`}
                    onChange={handleRadioChange}
                />
            </div>

            <CSSTransition
                in={selectedOption === 'trigger-criteria' || selectedOption === 'specific-time'}
                timeout={300}
                unmountOnExit
            >
                <div className="date-time-picker-section">
                    <div className="date-time-picker-wrapper">
                        <div className="date-picker-container picker-container">
                            <Label attributes={{ htmlFor: `asset-date-selector-${uniqueId || ''}` }}>
                                <Trans>{actionType === SelectorTypes.AssetAvailability ? t`Available Date` : t`Expiration Date`}</Trans>
                            </Label>
                            <Datetime
                                className="t-date-selector"
                                closeOnSelect
                                inputProps={{
                                    className: 'inputs',
                                    id: `asset-date-selector-${uniqueId || ''}`,
                                    placeholder: t`Select Date`,
                                    disabled: selectedOption === 'trigger-criteria' || isSectionDisabled
                                }}
                                isValidDate={(current) => {
                                    // Allow dates from today onwards
                                    return current.isSameOrAfter(moment(), 'day');
                                }}
                                onChange={(date) => {
                                    const dateStr = typeof date === 'string' ? date : date.format('MM/DD/YYYY');

                                    handleDateTimeUpdate(dateStr, currentTime, currentTimezone);
                                }}
                                renderDay={renderAccessibleDatetimeDay}
                                timeFormat={false}
                                value={currentDate}
                            />
                            <span className="bff-calendar icon" />
                        </div>
                        <div className="time-picker-container picker-container">
                            <Label attributes={{ htmlFor: `asset-time-selector-${uniqueId || ''}` }}>
                                <Trans>{actionType === SelectorTypes.AssetAvailability ? t`Available Time` : t`Expiration Time`}</Trans>
                            </Label>
                            <Datetime
                                closeOnSelect
                                dateFormat={false}
                                inputProps={{
                                    className: 'inputs',
                                    id: `asset-time-selector-${uniqueId || ''}`,
                                    placeholder: t`Select Time`,
                                    disabled: selectedOption === 'trigger-criteria' || isSectionDisabled
                                }}
                                onChange={(time) => {
                                    const timeStr = typeof time === 'string' ? time : time.format('hh:mm a');
                                    handleDateTimeUpdate(currentDate, timeStr, currentTimezone);
                                }}
                                renderDay={renderAccessibleDatetimeDay}
                                timeFormat="h:mm a"
                                value={currentTime}
                            />
                        </div>
                        <div className="timezone-container select-wrapper picker-container">
                            <Label attributes={{ htmlFor: `asset-timezone-selector-${uniqueId || ''}` }}>
                                <Trans>{t`Timezone`}</Trans>
                            </Label>
                            <ListDropdown
                                id={`asset-timezone-selector-${uniqueId || ''}`}
                                disabled={selectedOption === 'trigger-criteria' || isSectionDisabled}
                                onChange={(timezone) => {
                                    handleDateTimeUpdate(currentDate, currentTime, timezone ? timezone.value.toString() : '');
                                }}
                                options={moment.tz.names().map((name) => ({ value: name, label: name }))}
                                placeholder={t`Select Timezone`}
                                searchable
                                value={currentTimezone}
                                virtualizeOptions
                            />
                        </div>
                        <div className="delete-button-container">
                            <TextWarningButton
                                aria-label={t`clear date time values button`}
                                className="clear-datetime-values-button"
                                icon={FontIcons.Trash}
                                iconSize={16}
                                onClick={clearDateTimeData}
                            />
                        </div>
                    </div>

                </div>
            </CSSTransition>
        </div>
    );
};
